import { cabecalhosDoApp } from "@/lib/cabecalhos-do-app";
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
} from "amazon-cognito-identity-js";
import { detectPlatform } from "@/lib/utils/detect-platform";
import { getStoredUtmParams, limparUtmSource } from "@/lib/utm";
import { getFbp, getFbc, getGoogleAdsAttribution, getGaClientId, getConsentState } from "@/lib/services/tracking-service";
import { bumpFotoPerfilVersao, marcarFotoPerfilRemovida } from "@/lib/utils/cdn";
import { nomeProprio } from "@/lib/utils/nome";
import { normalizarEmail } from "@/lib/utils/email";
import { erroDoCognitoEmPortugues } from "@/lib/utils/erro-de-login";
import { fraseDoStatus, mensagemDaApi } from "@/lib/utils/erro-da-api";
import { fetchComTeto } from "@/lib/utils/fetch-com-teto";
import { limparMarcaDoAparelho } from "@/lib/tema/marca-no-aparelho";
import { esquecerTrilho } from "@/lib/onboarding/armazenamento";
// Auth types (previously in lib/types/auth.types.ts)
export interface RegisterPayload {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  birthday?: string;
  birthDay?: string;
  birthMonth?: string;
  birthYear?: string;
  displayName?: string;
  nick?: string;
  profileImage?: File | null;
  ref?: string;
  porOndeConheceu?: string;
}

export interface LoginPayload {
  email: string;
  password: string;
}

export interface AuthResponse {
  token: string;
  user: UserData;
}

/** O plano de cobrança que a tela do CPF destrava (`GET /usuario`, `cpf_obrigatorio_plano`). */
export interface PlanoQuePedeOCpf {
  nome: string;
  valor: number;
  /** MENSAL, BIMESTRAL, TRIMESTRAL, SEMESTRAL ou ANUAL. */
  periodo: string;
  /** O dia do mês em que a mensalidade vence. */
  dia_cobranca: number;
  /**
   * Quantos dias grátis o personal deu, enquanto o teste vale. O CPF é pedido no
   * teste também, desde o primeiro dia (05/10/2026), e a tela diz os dias grátis
   * antes do valor. Opcional porque a API de antes não manda.
   */
  teste_dias?: number | null;
  /** O último dia grátis (aaaa-mm-dd), enquanto o teste vale. */
  gratis_ate?: string | null;
  /** O último dia do teste grátis que já acabou (aaaa-mm-dd), ou null sem teste. */
  teste_terminou_em: string | null;
  /** Enquanto o teste vale, quando vence a 1ª mensalidade (aaaa-mm-dd). Opcional: a API de antes não manda. */
  primeira_vence_em?: string | null;
}

export interface UserData {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  displayName: string;
  /**
   * O apelido como está no banco: o slug do endereço público
   * (`/convite/<apelido>`, `/p/<apelido>`, `?ref=<apelido>`). Ele existe
   * separado do `displayName` porque aquele é nome de EXIBIÇÃO e passa por
   * `nomeProprio`, que sobe a primeira letra: montar link com ele gerava
   * `/convite/Paulo` e o cadastro caía em "Personal não encontrado".
   * Para montar link, use `apelidoDeUrl` de `@/lib/utils/apelido`.
   */
  apelido?: string;
  phoneNumber: string;
  birthday: string;
  profileImageUrl?: string;
  /**
   * Instante da última troca da foto de perfil, vindo do backend
   * (`usuario.foto_atualizada_em`). Nulo significa **sem foto**: é o que a casca
   * usa para desenhar as iniciais em vez de pedir uma imagem que não existe.
   */
  fotoAtualizadaEm?: string | null;
  /**
   * Por onde a pessoa entra: `"google"`, `"apple"` ou `"senha"`. Vem do
   * backend (`entra_com`) e decide o que o perfil promete ao trocar o e-mail,
   * e se "Trocar a senha" existe (quem entra pelo Google não tem senha).
   */
  entraCom?: "google" | "apple" | "senha";
  /**
   * Registro no CREF, só do personal (`012345-G/SP`). Opcional e nunca
   * conferido: sai ao lado do nome dele no PDF do treino e na avaliação.
   */
  cref?: string | null;
  /** Registro no CRN, de quem também é nutricionista (`CRN-3 12345`). Sai no PDF da dieta. */
  crn?: string | null;
  /**
   * Como o personal assina os contratos: a razão social da empresa, o CPF e o
   * CNPJ (os dois só com os dígitos). Preenchidos, vêm travados na hora de
   * assinar; os que faltam ficam guardados depois da primeira assinatura.
   */
  razaoSocial?: string | null;
  cpf?: string | null;
  cnpj?: string | null;
  createdAt: string;
  updatedAt: string;
  type?: string;
  verificado?: boolean;
  porOndeConheceu?: string | null;
  banido?: boolean;
  beta?: boolean;
  /** Tem o botão de mandar foto e vídeo no chat (`midia_no_chat` do GET /usuario, liberado por fatia). */
  midiaNoChat?: boolean;
  personal_bloqueado?: boolean;
  personal_nome?: string | null;
  personal_sobrenome?: string | null;
  personal_celular?: string | null;
  /** CRN do personal do aluno. Sai no PDF da dieta que o aluno baixa. */
  personal_crn?: string | null;
  /** CREF do personal do aluno. Sai no PDF do treino que o aluno baixa. */
  personal_cref?: string | null;
  aluno_desativado?: boolean;
  // Aluno bloqueado porque o personal está acima do limite de alunos do plano.
  bloqueado_por_limite?: boolean;
  // Validade do plano do aluno (controlada pelo personal). plano_vencido => acesso bloqueado.
  plano_vencido?: boolean;
  plano_vence_em?: string | null;
  // Cobrança que o aluno pode pagar pra liberar o acesso quando o plano venceu
  // (pagar avança a validade). Null => sem cobrança no app: só "falar com personal".
  cobranca_plano_vencido?: {
    invoice_url: string | null;
    pix_payload: string | null;
    valor: number;
    data_vencimento: string | null;
    /** A multa e os juros do contrato, que o Asaas soma no pagamento atrasado. */
    multa_percentual?: number | null;
    juros_mensal_percentual?: number | null;
  } | null;
  // Bloqueio automático por inadimplência (aluno com mensalidade vencida).
  inadimplente_bloqueado?: boolean;
  cobranca_vencida?: {
    invoice_url: string | null;
    pix_payload: string | null;
    valor: number;
    data_vencimento: string | null;
    multa_percentual?: number | null;
    juros_mensal_percentual?: number | null;
  } | null;
  // O personal ligou "Pedir o CPF no app" e o aluno está num plano sem CPF:
  // o porteiro mostra a tela de preencher o CPF antes do app.
  cpf_obrigatorio?: boolean;
  // O plano que o CPF destrava, escrito na mesma tela: o aluno quer saber quanto
  // vai pagar antes de dar o documento. No teste grátis, os dias grátis vêm junto.
  cpf_obrigatorio_plano?: PlanoQuePedeOCpf | null;
  tem_push_token?: boolean;
  // Preferência de tamanho da fonte (sincronizada entre dispositivos).
  // Opt-in dos lembretes de refeição por push (default true).
  permiteLembreteRefeicao?: boolean;
  // Conta autenticada via Google/Apple, senha derivada, não gerenciável. Usado
  // para esconder a opção de "alterar senha" nas configurações.
  login_social?: boolean;
}

export interface ApiError {
  status: number;
  message: string;
  data: any;
}

export interface UserTypeResponse {
  exists: boolean;
  type?: string;
  status?: string;
  data?: {
    status?: string;
    [key: string]: any;
  };
  error?: string;
}

class AuthService {
  private baseUrl =
    process.env.NEXT_PUBLIC_API_BASE_URL || "https://api.welltrainer.com.br";
  private cognitoClientId = process.env.NEXT_PUBLIC_COGNITO_CLIENT_ID || "";
  private cachedToken: string | null = null;
  private cachedTokenExpiry: number = 0;
  private pendingTokenRequest: Promise<string | null> | null = null;
  private pendingRefreshRequest: Promise<string | null> | null = null;
  private static TOKEN_CACHE_TTL = 300_000; // 5 minutes (Cognito tokens last 1h)


  private cognitoSignIn(email: string, password: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const userPool = new CognitoUserPool({
        UserPoolId:
          process.env.NEXT_PUBLIC_COGNITO_USER_POOL_ID || "us-east-1_QWYvcmVDY",
        ClientId: this.cognitoClientId,
      });

      // O Cognito diferencia maiúscula: "Maria@" e "maria@" são duas contas.
      const username = normalizarEmail(email);
      const cognitoUser = new CognitoUser({ Username: username, Pool: userPool });
      const authDetails = new AuthenticationDetails({
        Username: username,
        Password: password,
      });

      cognitoUser.authenticateUser(authDetails, {
        onSuccess: (session) => {
          try {
            const rt = session.getRefreshToken()?.getToken();
            if (rt && typeof localStorage !== "undefined") {
              localStorage.setItem("welltrainer_refresh_token", rt);
            }
          } catch {}
          resolve(session.getIdToken().getJwtToken());
        },
        /* O texto da AWS não sai daqui: a tela recebe a frase em português, e o
           nome do erro do Cognito vai em `data.codigo`, para a tela decidir o
           passo e para quem investiga. Vale para os três pedidos ao Cognito. */
        onFailure: (err) =>
          reject({
            status: 401,
            message: erroDoCognitoEmPortugues(err, "entrar"),
            data: { codigo: (err as { code?: string } | null)?.code ?? null },
          } as ApiError),
        newPasswordRequired: () =>
          reject({
            status: 401,
            message: "Troca de senha necessária.",
            data: null,
          } as ApiError),
      });
    });
  }

  async loginWithGoogle(googleToken: string): Promise<{
    token: string;
    user: UserData;
    cadastro_completo: boolean;
    usuario_novo: boolean;
  }> {
    const plataforma = detectPlatform();
    const utmStored = getStoredUtmParams();
    const utmSource = limparUtmSource(utmStored.utm_source);
    /* Faltavam as duas: o corpo do pedido já espalhava `googleAds` e lia
       `consent`, mas eles só eram declarados no cadastro por formulário. Como
       `const` não atravessa função, entrar com Google ou com Apple morria num
       ReferenceError antes de o pedido sair. */
    const googleAds = getGoogleAdsAttribution();
    const consent = getConsentState();
    const res = await fetch(`${this.baseUrl}/usuario/login-google`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...cabecalhosDoApp() },
      body: JSON.stringify({
        google_token: googleToken,
        plataforma,
        utm_source: utmSource,
        utm_medium: utmStored.utm_medium,
        utm_campaign: utmStored.utm_campaign,
        utm_term: utmStored.utm_term,
        utm_content: utmStored.utm_content,
        fbclid: utmStored.fbclid,
        gclid: utmStored.gclid,
        // Atribuição Meta CAPI (fbc reconstruído do fbclid se necessário)
        fbp: getFbp() || undefined,
        fbc: getFbc() || undefined,
        pagina_de_entrada: utmStored.pagina_de_entrada || undefined,
        referrer_externo: utmStored.referrer_externo || undefined,
        ref:
          utmStored.ref ||
          (typeof window !== "undefined" ? localStorage.getItem("welltrainer_ref") || undefined : undefined),
        // Atribuição Google Ads (iOS manda gbraid/wbraid no lugar do gclid),
        // client_id do GA4 e consentimento no momento do cadastro
        ...googleAds,
        ga_client_id: getGaClientId() || undefined,
        consent_ad_user_data: consent.ad_user_data || undefined,
        consent_ad_personalization: consent.ad_personalization || undefined,
      }),
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      if (res.status === 403 && data.bloqueado) {
        throw {
          status: 403,
          message: "Conta bloqueada.",
          data: { bloqueado: true },
        } as ApiError;
      }
      throw {
        status: res.status,
        /* 5xx (servidor fora, ou o ban silencioso da API): a frase de todo o app, e não o "Service Unavailable" do Nest. */
        message: res.status >= 500 ? fraseDoStatus(res.status) : data.mensagem || "Erro ao fazer login com Google.",
        data,
      } as ApiError;
    }

    const data = await res.json();
    const idToken = data.idToken;

    // Salvar refresh token se disponível
    if (data.refreshToken && typeof window !== "undefined") {
      localStorage.setItem("welltrainer_refresh_token", data.refreshToken);
    }

    // Buscar dados do usuário com o token
    const userRes = await fetch(
      `${this.baseUrl}/usuario?plataforma=${encodeURIComponent(plataforma)}`,
      {
        headers: { Authorization: idToken, ...cabecalhosDoApp() },
      },
    );

    if (!userRes.ok) {
      const userData2 = await userRes.json().catch(() => ({}));
      if (userRes.status === 403 && userData2.bloqueado) {
        throw {
          status: 403,
          message: "Conta bloqueada.",
          data: { bloqueado: true },
        } as ApiError;
      }
      throw {
        status: userRes.status,
        message: "Erro ao carregar dados do usuário.",
        data: null,
      } as ApiError;
    }

    const userData = await userRes.json();
    bumpFotoPerfilVersao(userData.id, userData.foto_atualizada_em);
    const user: UserData = {
      id: userData.id,
      email: userData.email,
      firstName: nomeProprio(userData.nome),
      lastName: nomeProprio(userData.sobrenome),
      displayName: nomeProprio(userData.apelido || userData.nome),
      apelido: userData.apelido ?? "",
      phoneNumber: userData.celular || "",
      birthday: userData.data_nascimento,
      profileImageUrl: undefined,
      // Quando a foto existe, e quando ela mudou: e o que faz o avatar
      // aparecer (e nao a reserva de imagem quebrada) em toda a casca do app.
      fotoAtualizadaEm: userData.foto_atualizada_em ?? null,
      entraCom: userData.entra_com ?? "senha",
      cref: userData.cref ?? null,
      crn: userData.crn ?? null,
      razaoSocial: userData.razao_social ?? null,
      cpf: userData.cpf ?? null,
      cnpj: userData.cnpj ?? null,
      createdAt: userData.created_at,
      updatedAt: userData.updated_at || userData.created_at,
      type: userData.tipo === "ALUNO" ? "TRAINEE" : userData.tipo,
      verificado: userData.verificado || false,
      porOndeConheceu: userData.por_onde_conheceu || null,
      banido: userData.banido || false,
      beta: userData.beta || false,
      midiaNoChat: userData.midia_no_chat === true,
      permiteLembreteRefeicao: userData.permite_lembrete_refeicao ?? true,
      login_social: userData.login_social ?? true,
    };

    this.saveAuthData({ token: idToken, user });

    return {
      token: idToken,
      user,
      cadastro_completo: data.cadastro_completo,
      usuario_novo: data.usuario_novo === true,
    };
  }

  async loginWithApple(
    appleCode: string,
    nome?: string,
    sobrenome?: string,
  ): Promise<{
    token: string;
    user: UserData;
    cadastro_completo: boolean;
    usuario_novo: boolean;
  }> {
    const plataforma = detectPlatform();
    const utmStored = getStoredUtmParams();
    const utmSource = limparUtmSource(utmStored.utm_source);
    /* Faltavam as duas: o corpo do pedido já espalhava `googleAds` e lia
       `consent`, mas eles só eram declarados no cadastro por formulário. Como
       `const` não atravessa função, entrar com Google ou com Apple morria num
       ReferenceError antes de o pedido sair. */
    const googleAds = getGoogleAdsAttribution();
    const consent = getConsentState();
    const res = await fetch(`${this.baseUrl}/usuario/login-apple`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...cabecalhosDoApp() },
      body: JSON.stringify({
        apple_code: appleCode,
        nome,
        sobrenome,
        plataforma,
        utm_source: utmSource,
        utm_medium: utmStored.utm_medium,
        utm_campaign: utmStored.utm_campaign,
        utm_term: utmStored.utm_term,
        utm_content: utmStored.utm_content,
        fbclid: utmStored.fbclid,
        gclid: utmStored.gclid,
        // Atribuição Meta CAPI (fbc reconstruído do fbclid se necessário)
        fbp: getFbp() || undefined,
        fbc: getFbc() || undefined,
        pagina_de_entrada: utmStored.pagina_de_entrada || undefined,
        referrer_externo: utmStored.referrer_externo || undefined,
        ref:
          utmStored.ref ||
          (typeof window !== "undefined" ? localStorage.getItem("welltrainer_ref") || undefined : undefined),
        // Atribuição Google Ads (iOS manda gbraid/wbraid no lugar do gclid),
        // client_id do GA4 e consentimento no momento do cadastro
        ...googleAds,
        ga_client_id: getGaClientId() || undefined,
        consent_ad_user_data: consent.ad_user_data || undefined,
        consent_ad_personalization: consent.ad_personalization || undefined,
      }),
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      if (res.status === 403 && data.bloqueado) {
        throw {
          status: 403,
          message: "Conta bloqueada.",
          data: { bloqueado: true },
        } as ApiError;
      }
      throw {
        status: res.status,
        /* 5xx (servidor fora, ou o ban silencioso da API): a frase de todo o app, e não o "Service Unavailable" do Nest. */
        message: res.status >= 500 ? fraseDoStatus(res.status) : data.mensagem || "Erro ao fazer login com Apple.",
        data,
      } as ApiError;
    }

    const data = await res.json();
    const idToken = data.idToken;

    if (data.refreshToken && typeof window !== "undefined") {
      localStorage.setItem("welltrainer_refresh_token", data.refreshToken);
    }

    const userRes = await fetch(
      `${this.baseUrl}/usuario?plataforma=${encodeURIComponent(plataforma)}`,
      {
        headers: { Authorization: idToken, ...cabecalhosDoApp() },
      },
    );

    if (!userRes.ok) {
      const userData2 = await userRes.json().catch(() => ({}));
      if (userRes.status === 403 && userData2.bloqueado) {
        throw {
          status: 403,
          message: "Conta bloqueada.",
          data: { bloqueado: true },
        } as ApiError;
      }
      throw {
        status: userRes.status,
        message: "Erro ao carregar dados do usuário.",
        data: null,
      } as ApiError;
    }

    const userData = await userRes.json();
    bumpFotoPerfilVersao(userData.id, userData.foto_atualizada_em);
    const user: UserData = {
      id: userData.id,
      email: userData.email,
      firstName: nomeProprio(userData.nome),
      lastName: nomeProprio(userData.sobrenome),
      displayName: nomeProprio(userData.apelido || userData.nome),
      apelido: userData.apelido ?? "",
      phoneNumber: userData.celular || "",
      birthday: userData.data_nascimento,
      profileImageUrl: undefined,
      // Quando a foto existe, e quando ela mudou: e o que faz o avatar
      // aparecer (e nao a reserva de imagem quebrada) em toda a casca do app.
      fotoAtualizadaEm: userData.foto_atualizada_em ?? null,
      entraCom: userData.entra_com ?? "senha",
      cref: userData.cref ?? null,
      crn: userData.crn ?? null,
      razaoSocial: userData.razao_social ?? null,
      cpf: userData.cpf ?? null,
      cnpj: userData.cnpj ?? null,
      createdAt: userData.created_at,
      updatedAt: userData.updated_at || userData.created_at,
      type: userData.tipo === "ALUNO" ? "TRAINEE" : userData.tipo,
      verificado: userData.verificado || false,
      porOndeConheceu: userData.por_onde_conheceu || null,
      banido: userData.banido || false,
      beta: userData.beta || false,
      midiaNoChat: userData.midia_no_chat === true,
      permiteLembreteRefeicao: userData.permite_lembrete_refeicao ?? true,
      login_social: userData.login_social ?? true,
    };

    this.saveAuthData({ token: idToken, user });

    return {
      token: idToken,
      user,
      cadastro_completo: data.cadastro_completo,
      usuario_novo: data.usuario_novo === true,
    };
  }

  async register(userData: RegisterPayload): Promise<AuthResponse> {
    /* A data de nascimento saiu do cadastro (o produto não usa) e virou opcional
       na API. Quem ainda manda (o perfil, o app antigo) continua funcionando;
       sem ela, o campo simplesmente não vai no formulário. */
    const { birthDay, birthMonth, birthYear } = userData;
    const birthday =
      birthDay && birthMonth && birthYear
        ? `${birthYear}-${String(birthMonth).padStart(2, "0")}-${String(birthDay).padStart(2, "0")}`
        : null;

    const formData = new FormData();
    formData.append("nome", userData.firstName);
    formData.append("sobrenome", userData.lastName);
    formData.append("email", normalizarEmail(userData.email));
    formData.append("senha", userData.password);
    formData.append("celular", userData.phoneNumber);
    formData.append("apelido", userData.displayName || userData.firstName);
    if (birthday) formData.append("dataNascimento", birthday);
    formData.append("plataforma", detectPlatform());
    if (userData.profileImage) {
      formData.append("fotoPerfil", userData.profileImage);
    }
    if (userData.ref) {
      formData.append("ref", userData.ref);
    }
    if (userData.porOndeConheceu) {
      formData.append("porOndeConheceu", userData.porOndeConheceu);
    }

    // Enviar UTMs armazenados do sessionStorage (com sanitização anti-UTMify)
    const utmParams = getStoredUtmParams();
    for (const [key, value] of Object.entries(utmParams)) {
      if (!value) continue;
      const limpo = key === "utm_source" ? limparUtmSource(value) : value;
      if (limpo) formData.append(key, limpo);
    }

    // Capturar cookies Meta Pixel para atribuição CAPI. getFbc() reconstrói
    // o _fbc a partir do fbclid persistido (fb.1.{Date.now()}.{fbclid}, ts em
    // ms) quando o cookie não existe, ex.: pixel bloqueado ou fbclid vindo
    // do domínio raiz via cookie wt_fbclid.
    const fbp = getFbp();
    const fbc = getFbc();
    if (fbp) formData.append("_fbp", fbp);
    if (fbc) formData.append("_fbc", fbc);

    // Atribuição Google Ads (gclid/gbraid/wbraid/gad_* de URL, localStorage ou
    // cookies wt_* do domínio raiz), client_id do GA4 e consentimento. Só
    // acrescenta o que ainda não veio pelos UTMs armazenados.
    const googleAds = getGoogleAdsAttribution();
    for (const [key, value] of Object.entries(googleAds)) {
      if (value && !formData.has(key)) formData.append(key, value);
    }
    const gaClientId = getGaClientId();
    if (gaClientId) formData.append("ga_client_id", gaClientId);
    const consent = getConsentState();
    if (consent.ad_user_data) formData.append("consent_ad_user_data", consent.ad_user_data);
    if (consent.ad_personalization) formData.append("consent_ad_personalization", consent.ad_personalization);

    // external_id removido, agora enviado via dataLayer no tracking-service

    const response = await fetch(`${this.baseUrl}/usuario`, {
      method: "POST",
      /* Sem Content-Type: o navegador põe o do FormData. */
      headers: cabecalhosDoApp(),
      body: formData,
    });

    if (!response.ok) {
      let message = "Erro ao cadastrar. Tente novamente.";
      try {
        const data = await response.json();
        message = data.message || data.error || message;
      } catch {}
      /* 5xx (servidor fora, ou o ban silencioso da API): a frase de todo o app, e não o "Service Unavailable" do Nest. */
      if (response.status >= 500) message = fraseDoStatus(response.status);
      throw { status: response.status, message, data: null } as ApiError;
    }

    const data = await response.json();

    // Authenticate on Cognito to get the token
    const token = await this.cognitoSignIn(userData.email, userData.password);

    const user: UserData = {
      id: data.id || data.user?.id || "",
      email: normalizarEmail(userData.email),
      firstName: nomeProprio(userData.firstName),
      lastName: nomeProprio(userData.lastName),
      displayName: nomeProprio(userData.displayName || userData.firstName),
      phoneNumber: userData.phoneNumber,
      birthday: birthday ?? "",
      profileImageUrl: data.profileImageUrl || data.user?.profileImageUrl,
      createdAt: data.createdAt || new Date().toISOString(),
      updatedAt: data.updatedAt || new Date().toISOString(),
      type: data.type || data.user?.type || "PERSONAL",
      verificado: false,
      porOndeConheceu: userData.porOndeConheceu || null,
    };

    this.saveAuthData({ token, user });
    return { token, user };
  }

  async login(credentials: LoginPayload): Promise<AuthResponse> {
    const token = await this.cognitoSignIn(
      credentials.email,
      credentials.password,
    );

    const plataforma = detectPlatform();
    const res = await fetch(
      `${this.baseUrl}/usuario?plataforma=${encodeURIComponent(plataforma)}`,
      {
        headers: { Authorization: token, ...cabecalhosDoApp() },
      },
    );

    if (!res.ok) {
      /* A senha entrou no Cognito, mas a conta não abriu aqui (o login aponta
         para uma conta apagada, ou a conta está bloqueada). A sessão que o SDK
         acabou de guardar sai do aparelho: deixada lá, ela ganhava do próximo
         login, pelo Google ou pela Apple, em todo pedido. Ver
         `sessaoDoCognitoDeOutraConta`. */
      this.esquecerSessaoDoCognito();
      const data = await res.json().catch(() => ({}));
      if (res.status === 403 && data.bloqueado) {
        throw {
          status: 403,
          message: "Conta bloqueada.",
          data: { bloqueado: true },
        } as ApiError;
      }
      throw {
        status: res.status,
        /* 5xx (servidor fora, ou o ban silencioso da API): a frase de todo o app, e não o "Service Unavailable" do Nest. */
        message: res.status >= 500 ? fraseDoStatus(res.status) : data.message || "Erro ao carregar dados do usuário.",
        data,
      } as ApiError;
    }

    const data = await res.json();
    const user: UserData = {
      id: data.id,
      email: data.email,
      firstName: nomeProprio(data.nome),
      lastName: nomeProprio(data.sobrenome),
      displayName: nomeProprio(data.apelido || data.nome),
      apelido: data.apelido ?? "",
      phoneNumber: data.celular,
      birthday: data.data_nascimento,
      profileImageUrl: undefined,
      // Quando a foto existe, e quando ela mudou: e o que faz o avatar
      // aparecer (e nao a reserva de imagem quebrada) em toda a casca do app.
      fotoAtualizadaEm: data.foto_atualizada_em ?? null,
      entraCom: data.entra_com ?? "senha",
      cref: data.cref ?? null,
      crn: data.crn ?? null,
      razaoSocial: data.razao_social ?? null,
      cpf: data.cpf ?? null,
      cnpj: data.cnpj ?? null,
      createdAt: data.created_at,
      updatedAt: data.updated_at || data.created_at,
      type: data.tipo === "ALUNO" ? "TRAINEE" : data.tipo,
      verificado: data.verificado || false,
      porOndeConheceu: data.por_onde_conheceu || null,
      banido: data.banido || false,
      beta: data.beta || false,
      midiaNoChat: data.midia_no_chat === true,
      permiteLembreteRefeicao: data.permite_lembrete_refeicao ?? true,
      login_social: data.login_social || false,
    };

    this.saveAuthData({ token, user });
    return { token, user };
  }

  /**
   * Login por QR code (desktop): recebe os tokens já emitidos pela API (após o
   * celular aprovar a sessão) e hidrata a sessão exatamente como o login normal
   *, salva os tokens, busca os dados do usuário e persiste. O idToken é um JWT
   * próprio da API (issuer .../qr-login); o refresh usa /usuario/refresh-token,
   * que reconhece esse token, então o fluxo de refresh existente continua valendo.
   */
  async loginComQrTokens(
    idToken: string,
    refreshToken?: string,
  ): Promise<AuthResponse> {
    if (typeof window !== "undefined") {
      localStorage.setItem("welltrainer_token", idToken);
      if (refreshToken) {
        localStorage.setItem("welltrainer_refresh_token", refreshToken);
      }
    }
    // Invalida o cache de token pra não herdar um token de outra sessão neste browser.
    this.cachedToken = null;
    this.cachedTokenExpiry = 0;

    const plataforma = detectPlatform();
    const res = await fetch(
      `${this.baseUrl}/usuario?plataforma=${encodeURIComponent(plataforma)}`,
      { headers: { Authorization: idToken, ...cabecalhosDoApp() } },
    );

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      if (res.status === 403 && data.bloqueado) {
        throw {
          status: 403,
          message: "Conta bloqueada.",
          data: { bloqueado: true },
        } as ApiError;
      }
      throw {
        status: res.status,
        message: mensagemDaApi(data, "Erro ao carregar dados do usuário."),
        data,
      } as ApiError;
    }

    const data = await res.json();
    bumpFotoPerfilVersao(data.id, data.foto_atualizada_em);
    const user: UserData = {
      id: data.id,
      email: data.email,
      firstName: nomeProprio(data.nome),
      lastName: nomeProprio(data.sobrenome),
      displayName: nomeProprio(data.apelido || data.nome),
      apelido: data.apelido ?? "",
      phoneNumber: data.celular,
      birthday: data.data_nascimento,
      profileImageUrl: undefined,
      // Quando a foto existe, e quando ela mudou: e o que faz o avatar
      // aparecer (e nao a reserva de imagem quebrada) em toda a casca do app.
      fotoAtualizadaEm: data.foto_atualizada_em ?? null,
      entraCom: data.entra_com ?? "senha",
      cref: data.cref ?? null,
      crn: data.crn ?? null,
      razaoSocial: data.razao_social ?? null,
      cpf: data.cpf ?? null,
      cnpj: data.cnpj ?? null,
      createdAt: data.created_at,
      updatedAt: data.updated_at || data.created_at,
      type: data.tipo === "ALUNO" ? "TRAINEE" : data.tipo,
      verificado: data.verificado || false,
      porOndeConheceu: data.por_onde_conheceu || null,
      banido: data.banido || false,
      beta: data.beta || false,
      midiaNoChat: data.midia_no_chat === true,
      login_social: data.login_social || false,
    };

    this.saveAuthData({ token: idToken, user });
    return { token: idToken, user };
  }

  async forgotPassword(email: string): Promise<{ message: string }> {
    return new Promise((resolve, reject) => {
      const userPool = new CognitoUserPool({
        UserPoolId:
          process.env.NEXT_PUBLIC_COGNITO_USER_POOL_ID || "us-east-1_QWYvcmVDY",
        ClientId: this.cognitoClientId,
      });

      const cognitoUser = new CognitoUser({ Username: normalizarEmail(email), Pool: userPool });

      cognitoUser.forgotPassword({
        onSuccess: () =>
          resolve({ message: "Código de verificação enviado para seu email" }),
        onFailure: (err) =>
          reject({
            status: 400,
            message: erroDoCognitoEmPortugues(err, "pedir-codigo"),
            data: { codigo: (err as { code?: string } | null)?.code ?? null },
          } as ApiError),
      });
    });
  }

  async resetPassword(
    email: string,
    code: string,
    newPassword: string,
  ): Promise<{ message: string }> {
    return new Promise((resolve, reject) => {
      const userPool = new CognitoUserPool({
        UserPoolId:
          process.env.NEXT_PUBLIC_COGNITO_USER_POOL_ID || "us-east-1_QWYvcmVDY",
        ClientId: this.cognitoClientId,
      });

      const cognitoUser = new CognitoUser({ Username: normalizarEmail(email), Pool: userPool });

      cognitoUser.confirmPassword(code, newPassword, {
        onSuccess: () => resolve({ message: "Senha alterada com sucesso" }),
        onFailure: (err) =>
          reject({
            status: 400,
            message: erroDoCognitoEmPortugues(err, "trocar-senha"),
            data: { codigo: (err as { code?: string } | null)?.code ?? null },
          } as ApiError),
      });
    });
  }

  async refreshUser(): Promise<UserData | null> {
    let token = await this.getValidToken();
    if (!token) return null;

    let res = await fetch(`${this.baseUrl}/usuario`, {
      headers: { Authorization: token, ...cabecalhosDoApp() },
    });

    // 401: tenta refrescar o token e refazer o request. Se nem o refresh recupera,
    // sessao perdida, mandar pra /login em vez de engolir silenciosamente.
    if (res.status === 401) {
      try {
        token = await this.forceRefreshToken();
        if (token) {
          res = await fetch(`${this.baseUrl}/usuario`, {
            headers: { Authorization: token, ...cabecalhosDoApp() },
          });
        }
      } catch {}
      if (res.status === 401) {
        if (!token) this.handleSessionLost();
        return null;
      }
    }

    if (res.status === 403) {
      const data = await res.json().catch(() => ({}));
      if (data.bloqueado) {
        // Retornar um user com banido=true para que o AuthGuard mostre BanScreen
        return { banido: true } as UserData;
      }
      if (data.aluno_desativado) {
        return { aluno_desativado: true } as UserData;
      }
      if (data.bloqueado_por_limite) {
        // Personal acima do limite de alunos. AuthGuard mostra AlunoBloqueadoLimiteScreen.
        return {
          bloqueado_por_limite: true,
          personal_nome: data.personal_nome ?? null,
          personal_sobrenome: data.personal_sobrenome ?? null,
          personal_celular: data.personal_celular ?? null,
        } as UserData;
      }
      if (data.personal_bloqueado) {
        // Retornar user com flag para que o AuthGuard mostre PersonalBloqueadoScreen
        return {
          personal_bloqueado: true,
          personal_nome: data.personal_nome ?? null,
          personal_sobrenome: data.personal_sobrenome ?? null,
          personal_celular: data.personal_celular ?? null,
        } as UserData;
      }
      if (data.plano_vencido) {
        // Plano do aluno vencido. AuthGuard mostra PlanoVencidoScreen.
        return {
          plano_vencido: true,
          plano_vence_em: data.plano_vence_em ?? null,
          cobranca_plano_vencido: data.cobranca_plano_vencido ?? null,
          personal_nome: data.personal_nome ?? null,
          personal_sobrenome: data.personal_sobrenome ?? null,
          personal_celular: data.personal_celular ?? null,
        } as UserData;
      }
      if (data.inadimplente) {
        // Mensalidade em atraso. AuthGuard mostra MensalidadeAtrasadaScreen.
        return {
          inadimplente_bloqueado: true,
          cobranca_vencida: {
            invoice_url: data.invoice_url ?? null,
            pix_payload: data.pix_payload ?? null,
            valor: data.valor_vencido ?? 0,
            data_vencimento: data.data_vencimento ?? null,
            multa_percentual: data.multa_percentual ?? null,
            juros_mensal_percentual: data.juros_mensal_percentual ?? null,
          },
          personal_nome: data.personal_nome ?? null,
          personal_sobrenome: data.personal_sobrenome ?? null,
          personal_celular: data.personal_celular ?? null,
        } as UserData;
      }
      return null;
    }

    if (!res.ok) return null;

    const data = await res.json();
    // Semeia a versão da foto (cache-buster), cobre cross-device do próprio usuário.
    bumpFotoPerfilVersao(data.id, data.foto_atualizada_em);
    const user: UserData = {
      id: data.id,
      email: data.email,
      firstName: nomeProprio(data.nome),
      lastName: nomeProprio(data.sobrenome),
      displayName: nomeProprio(data.apelido || data.nome),
      apelido: data.apelido ?? "",
      phoneNumber: data.celular,
      birthday: data.data_nascimento,
      profileImageUrl: undefined,
      // Quando a foto existe, e quando ela mudou: e o que faz o avatar
      // aparecer (e nao a reserva de imagem quebrada) em toda a casca do app.
      fotoAtualizadaEm: data.foto_atualizada_em ?? null,
      entraCom: data.entra_com ?? "senha",
      cref: data.cref ?? null,
      crn: data.crn ?? null,
      razaoSocial: data.razao_social ?? null,
      cpf: data.cpf ?? null,
      cnpj: data.cnpj ?? null,
      createdAt: data.created_at,
      updatedAt: data.updated_at || data.created_at,
      type: data.tipo === "ALUNO" ? "TRAINEE" : data.tipo,
      verificado: data.verificado || false,
      porOndeConheceu: data.por_onde_conheceu || null,
      banido: data.banido || false,
      beta: data.beta || false,
      midiaNoChat: data.midia_no_chat === true,
      personal_bloqueado: data.personal_bloqueado || false,
      personal_nome: data.personal_nome ?? null,
      personal_sobrenome: data.personal_sobrenome ?? null,
      personal_celular: data.personal_celular ?? null,
      personal_crn: data.personal_crn ?? null,
      personal_cref: data.personal_cref ?? null,
      aluno_desativado: data.aluno_desativado || false,
      bloqueado_por_limite: data.bloqueado_por_limite || false,
      plano_vencido: data.plano_vencido || false,
      plano_vence_em: data.plano_vence_em ?? null,
      cobranca_plano_vencido: data.cobranca_plano_vencido ?? null,
      inadimplente_bloqueado: data.inadimplente_bloqueado || false,
      cobranca_vencida: data.cobranca_vencida ?? null,
      cpf_obrigatorio: data.cpf_obrigatorio || false,
      cpf_obrigatorio_plano: data.cpf_obrigatorio_plano ?? null,
      tem_push_token: data.tem_push_token || false,
      permiteLembreteRefeicao: data.permite_lembrete_refeicao ?? true,
      login_social: data.login_social || false,
    };

    try {
      if (typeof window !== "undefined") {
        localStorage.setItem("welltrainer_user", JSON.stringify(user));
      }
    } catch {}

    /* O servidor é quem sabe se esta conta tem algum aparelho registrado. Se
       ele diz que não, e este aparelho tem token, a marca guardada aqui está
       mentindo (o token morreu e foi apagado, ou outra conta ficou com ele) e o
       registro acontece de novo. Sem isto, quem perdeu o registro só voltava a
       receber push depois de sair e entrar. */
    if (user.tem_push_token === false && typeof window !== "undefined") {
      import("../utils/push-registro")
        .then(({ sincronizarPushToken }) => sincronizarPushToken({ forcar: true }))
        .catch(() => {});
    }

    return user;
  }

  /**
   * `falhou` separa "não deu para conferir" de "não existe".
   *
   * Sem essa diferença, servidor fora e rede ruim respondiam a mesma coisa que
   * um e-mail errado, e a porta dizia "Conta não encontrada" para quem tem
   * conta. Nunca lança: quem só quer saber se existe continua lendo `existe`.
   */
  async checkEmailExists(email: string): Promise<{
    existe: boolean;
    tipo?: string;
    status?: string;
    checkout_sem_senha?: boolean;
    falhou?: boolean;
  }> {
    try {
      const response = await fetchComTeto(
        `${this.baseUrl}/usuario/existe?email=${encodeURIComponent(normalizarEmail(email))}`,
      );
      if (!response.ok) return { existe: false, falhou: true };
      return await response.json();
    } catch {
      return { existe: false, falhou: true };
    }
  }

  async criarSenhaCheckout(
    email: string,
    senha: string,
  ): Promise<{ mensagem: string; idToken: string; refreshToken: string }> {
    const response = await fetch(
      `${this.baseUrl}/usuario/criar-senha-checkout`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: normalizarEmail(email), senha }),
      },
    );
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.mensagem || "Erro ao criar senha.");
    }
    const data = await response.json();

    // Auto-login: salvar tokens
    if (data.idToken) {
      localStorage.setItem("welltrainer_token", data.idToken);
    }
    if (data.refreshToken) {
      localStorage.setItem("welltrainer_refresh_token", data.refreshToken);
    }

    return data;
  }

  /**
   * Pede os seis números da primeira senha (o aluno convidado e quem assinou
   * pela página de vendas), com a chave `primeira_senha_com_codigo` ligada.
   *
   * A resposta é a MESMA para qualquer e-mail: quem manda o código é o Cognito,
   * e o que confirma que a conta é da pessoa é ela digitar o que chegou. O teto
   * é de 15s, e não os 8s do `fetchComTeto`, porque o servidor cria o login e
   * espera o Cognito despachar o e-mail antes de responder: cortar antes faria a
   * tela dizer que falhou com o e-mail já a caminho, e a pessoa pediria outro.
   */
  async pedirCodigoDaPrimeiraSenha(email: string): Promise<{ mensagem: string }> {
    return this.pedirSemLogin(
      "/usuario/primeira-senha/codigo",
      { email: normalizarEmail(email) },
      "Não conseguimos mandar o código. Tente de novo em alguns minutos.",
    );
  }

  /**
   * Cria a primeira senha com o código do e-mail. Não devolve token: com o 200,
   * a tela entra pelo `login` de sempre, que monta a sessão do Cognito e leva cada
   * um para o painel dele. Código errado ou vencido volta com
   * `data.codigo = "CODIGO_ERRADO_OU_VENCIDO"`, que é o que manda a tela de volta
   * para o passo do código.
   */
  async criarPrimeiraSenha(
    email: string,
    codigo: string,
    senha: string,
  ): Promise<{ mensagem: string }> {
    return this.pedirSemLogin(
      "/usuario/primeira-senha",
      { email: normalizarEmail(email), codigo, senha },
      "Não conseguimos criar a senha. Tente de novo.",
    );
  }

  /** POST numa rota de conta sem login, com o erro já na forma de `ApiError`. */
  private async pedirSemLogin(
    rota: string,
    corpo: Record<string, string>,
    fraseGenerica: string,
  ): Promise<{ mensagem: string }> {
    let response: Response;
    try {
      response = await fetchComTeto(
        `${this.baseUrl}${rota}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(corpo),
        },
        15000,
      );
    } catch {
      throw {
        status: 0,
        message: "Sem conexão com o servidor. Confira a internet e tente de novo.",
        data: null,
      } as ApiError;
    }
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw {
        status: response.status,
        message: data.mensagem || fraseGenerica,
        data,
      } as ApiError;
    }
    return data;
  }

  async checkPhoneExists(
    celular: string,
  ): Promise<{ existe: boolean; falhou?: boolean }> {
    try {
      const response = await fetchComTeto(
        `${this.baseUrl}/usuario/existe?celular=${encodeURIComponent(celular)}`,
      );
      if (!response.ok) return { existe: false, falhou: true };
      return await response.json();
    } catch {
      return { existe: false, falhou: true };
    }
  }

  async activateTraineeAccount(
    email: string,
    password: string,
  ): Promise<{ mensagem: string }> {
    const res = await fetch(`${this.baseUrl}/usuario/ativar-aluno`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: normalizarEmail(email), senha: password }),
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw {
        status: res.status,
        message: data.mensagem || "Erro ao ativar conta.",
        data,
      } as ApiError;
    }

    return res.json();
  }

  /**
   * Chamado pelos apiFetch dos services quando um 401 persiste apos a tentativa
   * de forceRefreshToken. Significa que nem o Cognito SDK nem o fallback via API
   * conseguiram renovar, sessao esta perdida. Limpa tudo e manda pra /login.
   *
   * Deduplicado: varios fetches paralelos tendem a cair aqui ao mesmo tempo,
   * mas so o primeiro redireciona. Evita loop infinito em /login.
   *
   * Caso especial: se está em modo "atuar como aluno" (impersonation) e o
   * token JWT custom expirou (24h), restaura o token original do personal
   * em vez de mandar pra /login.
   */
  handleSessionLost(): void {
    if (typeof window === "undefined") return;
    /* Já estamos saindo por vontade da pessoa: as requisições que ficaram em
       voo voltam 401 logo depois de o token ser apagado, e sem esta saída elas
       trocavam o destino para "/login?sessao_expirada=1". A pessoa clicava em
       Sair e chegava no login com um aviso de que a sessão tinha caído. */
    if (this.saindo) return;
    /* `/entrar/apple` é a mesma tela de entrar (a volta da Apple no celular):
       um 401 de token velho ali no meio levaria a pessoa para o `/login` antes
       de o código da Apple virar sessão. */
    if (
      window.location.pathname === "/login" ||
      window.location.pathname === "/" ||
      window.location.pathname.startsWith("/entrar/")
    )
      return;

    try {
      const origemRaw = localStorage.getItem(
        "welltrainer_impersonation_origem",
      );
      if (origemRaw) {
        const origem = JSON.parse(origemRaw) as {
          token: string;
          user: string;
          refresh_token: string | null;
        };
        if (origem.token)
          localStorage.setItem("welltrainer_token", origem.token);
        if (origem.user) localStorage.setItem("welltrainer_user", origem.user);
        if (origem.refresh_token)
          localStorage.setItem(
            "welltrainer_refresh_token",
            origem.refresh_token,
          );
        localStorage.removeItem("welltrainer_impersonation_origem");
        localStorage.removeItem("welltrainer_impersonation_atual");
        this.cachedToken = null;
        this.cachedTokenExpiry = 0;
        // Volta pro dashboard do personal e mostra um aviso (via query param)
        window.location.href = "/dashboard/personal?impersonation_expirada=1";
        return;
      }
    } catch {}

    this.logout();
    try {
      window.location.href = "/login?sessao_expirada=1";
    } catch {}
  }

  /**
   * Verdadeiro entre o clique em "Sair" e o carregamento do login.
   *
   * Serve para o tratamento de 401 saber que a sessão não caiu: ela foi
   * encerrada de propósito.
   */
  private saindo = false;

  /**
   * Avisa a API que este aparelho deixou de ser desta conta. Sem isso a conta
   * deslogada continua dona do token em usuario_push_token e o push segue
   * chegando no device, inclusive quando ninguém mais loga nele. Precisa rodar
   * ANTES de limpar o localStorage (o JWT some junto), e com keepalive pra
   * sobreviver ao redirect que costuma vir logo depois do logout.
   */
  private avisarLogoutDoDispositivo(): void {
    try {
      if (typeof window === "undefined" || typeof localStorage === "undefined") return;
      const jwt = localStorage.getItem("welltrainer_token");
      const pushToken = localStorage.getItem("welltrainer_push_token");
      if (!jwt || !pushToken) return;

      const baseUrl =
        process.env.NEXT_PUBLIC_API_BASE_URL || "https://api.welltrainer.com.br";
      fetch(`${baseUrl}/usuario/push-token`, {
        method: "DELETE",
        headers: {
          ...cabecalhosDoApp(),
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({ push_token: pushToken }),
        keepalive: true,
      }).catch(() => {});
    } catch {}
  }

  logout(): void {
    this.saindo = true;
    this.avisarLogoutDoDispositivo();
    this.cachedToken = null;
    this.cachedTokenExpiry = 0;
    try {
      const userPool = new CognitoUserPool({
        UserPoolId:
          process.env.NEXT_PUBLIC_COGNITO_USER_POOL_ID || "us-east-1_QWYvcmVDY",
        ClientId: this.cognitoClientId,
      });
      const cognitoUser = userPool.getCurrentUser();
      if (cognitoUser) cognitoUser.signOut();
    } catch {}

    try {
      if (
        typeof window !== "undefined" &&
        typeof localStorage !== "undefined"
      ) {
        localStorage.removeItem("welltrainer_token");
        localStorage.removeItem("welltrainer_user");
        localStorage.removeItem("welltrainer_refresh_token");
        localStorage.removeItem("welltrainer_assinatura_status");
        // Marca de sync do push token: o token é do APARELHO e continua válido,
        // mas a próxima conta que logar precisa registrá-lo no backend dela.
        localStorage.removeItem("welltrainer_push_token_synced");
        /* A marca antiga de "notificações desligadas" não dizia de quem era, e
           calava a próxima conta a entrar no aparelho. A escolha de cada conta
           vive em welltrainer_push_desligado::<usuarioId> e NÃO sai daqui: ela
           é da pessoa, e vale de novo quando ela voltar. */
        localStorage.removeItem("welltrainer_push_desligado");
        /* Modo "acessar como aluno": quem sai pelo botão Sair, e não pela faixa
           amarela, deixava o marcador no aparelho. A conta seguinte entrava com
           a faixa no nome de outra pessoa e sem a anamnese pendente dela. */
        localStorage.removeItem("welltrainer_impersonation_atual");
        localStorage.removeItem("welltrainer_impersonation_origem");
        /* A Central de Ativação saiu do produto. As duas chaves continuam sendo
           APAGADAS no logout, para o navegador de quem já usou o app não ficar
           carregando marcador morto. */

        localStorage.removeItem("welltrainer_onboarding_done");
        localStorage.removeItem("welltrainer_onboarding_pending");
        /* A leitura guardada do passo a passo do começo, de todas as contas que
           passaram por este aparelho: ela diz se o painel abre, e herdar a de
           outra pessoa é destrancar (ou trancar) a conta errada. */
        esquecerTrilho();
        localStorage.removeItem("welltrainer_pending_brand");
        /* Limpar a marca guardada no aparelho: as CINCO chaves, pela lista do
           `brand-theme-context`. Aqui estavam três escritas à mão, e o logo
           claro e a escala do personal anterior continuavam no celular: quem
           entrasse depois, no mesmo aparelho, via marca de outra conta. */
        limparMarcaDoAparelho();
      }
    } catch (error) {
      console.error("Error during logout:", error);
    }
  }

  /**
   * Tira a foto da própria conta, e ela some da tela na mesma hora.
   *
   * A chave no S3 é fixa, então apagar o arquivo não muda o endereço e o
   * navegador continuaria servindo a foto removida. `marcarFotoPerfilRemovida`
   * tira o recorte guardado na sessão e avança o `?v=`: a URL nova responde
   * "não existe" e o avatar cai nas iniciais, no app inteiro.
   */
  async removerFotoPerfil(usuarioId: string): Promise<void> {
    const { apiFetch } = await import("../api");
    await apiFetch("/usuario/foto-perfil", { method: "DELETE" });
    marcarFotoPerfilRemovida(usuarioId);
  }

  async deletarConta(): Promise<void> {
    const { apiFetch } = await import("../api");
    await apiFetch("/usuario", { method: "DELETE" });
    this.logout();
  }

  /**
   * Altera a senha do usuário logado. O backend valida a senha atual no Cognito
   * antes de definir a nova. Rejeita (ApiError com mensagem do servidor) quando a
   * senha atual está incorreta ou a conta usa login social.
   */
  async alterarSenha(
    senhaAtual: string,
    novaSenha: string,
  ): Promise<{ mensagem: string }> {
    const { apiFetch } = await import("../api");
    return apiFetch("/usuario/senha", {
      method: "PATCH",
      body: JSON.stringify({ senha_atual: senhaAtual, nova_senha: novaSenha }),
    });
  }

  getCognitoToken(): string | null {
    try {
      if (typeof window === "undefined") return null;
      const token = localStorage.getItem("welltrainer_token");
      if (!token || !token.startsWith("eyJ")) return null;
      return token;
    } catch {
      return null;
    }
  }

  /**
   * Tenta renovar o token via API usando o refresh token armazenado.
   */
  private refreshTokenViaApi(): Promise<string | null> {
    if (this.pendingRefreshRequest) return this.pendingRefreshRequest;

    this.pendingRefreshRequest = (async () => {
      try {
        if (typeof window === "undefined") return null;
        const refreshToken = localStorage.getItem("welltrainer_refresh_token");
        if (!refreshToken) return null;

        const res = await fetch(`${this.baseUrl}/usuario/refresh-token`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });

        if (!res.ok) {
          // Refresh token inválido/expirado - limpar
          localStorage.removeItem("welltrainer_refresh_token");
          return null;
        }

        const data = await res.json();
        if (data.idToken) {
          this.cachedToken = data.idToken;
          this.cachedTokenExpiry = Date.now() + AuthService.TOKEN_CACHE_TTL;
          localStorage.setItem("welltrainer_token", data.idToken);
          return data.idToken;
        }
        return null;
      } catch {
        return null;
      }
    })().finally(() => {
      this.pendingRefreshRequest = null;
    });

    return this.pendingRefreshRequest;
  }

  /**
   * Verifica se um JWT está expirado (ou prestes a expirar em 60s).
   */
  private isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.exp * 1000 < Date.now() + 60_000; // 60s de margem
    } catch {
      return true;
    }
  }

  /**
   * A sessão do SDK do Cognito é de uma conta que não é a que está no aparelho
   * (`welltrainer_user`)? Sem as duas pontas para comparar, não decide nada.
   */
  private sessaoDoCognitoDeOutraConta(session: any): boolean {
    try {
      const dono = session.getIdToken().decodePayload()?.["custom:userId"];
      const doAparelho = this.getCurrentUser()?.id;
      return Boolean(dono && doAparelho && dono !== doAparelho);
    } catch {
      return false;
    }
  }

  /** Apaga do aparelho a sessão que o SDK do Cognito guardou na entrada por senha. */
  private esquecerSessaoDoCognito(): void {
    try {
      const userPool = new CognitoUserPool({
        UserPoolId:
          process.env.NEXT_PUBLIC_COGNITO_USER_POOL_ID || "us-east-1_QWYvcmVDY",
        ClientId: this.cognitoClientId,
      });
      userPool.getCurrentUser()?.signOut();
    } catch {}
  }

  /**
   * Força a invalidação do cache e re-busca um token fresco via Cognito SDK
   * (ou API refresh para logins Google/Apple). Usado pelo api.ts no handler de 401.
   */
  async forceRefreshToken(): Promise<string | null> {
    this.cachedToken = null;
    this.cachedTokenExpiry = 0;
    /* No modo "atuar como aluno" não há o que renovar: o token é da API, dura
       24 horas e não tem refresh. Devolver o MESMO token repetia o 401 com ele, e
       a tela ficava presa no modo aluno dando erro em tudo, porque quem chama só
       encerra a sessão (`handleSessionLost`, que devolve o personal ao painel
       dele) quando não vem token novo. A API recusa esse token quando o aluno
       deixa de ser do personal ou o personal é bloqueado (24/10/2026). */
    if (
      typeof window !== "undefined" &&
      localStorage.getItem("welltrainer_impersonation_atual")
    )
      return null;
    return this.getValidToken();
  }

  /**
   * Returns a valid (non-expired) id token.
   * Uses Cognito's getSession() which automatically refreshes
   * the token using the stored refresh token when expired.
   * Falls back to API refresh for Google/Apple login users.
   */
  getValidToken(): Promise<string | null> {
    // Return cached token if still valid AND the JWT itself isn't expired.
    // O cache TTL de 5min não garante que o JWT ainda está válido, se o token
    // tiver sido emitido perto do cache, pode já estar expirado dentro da janela.
    if (
      this.cachedToken &&
      Date.now() < this.cachedTokenExpiry &&
      !this.isTokenExpired(this.cachedToken)
    ) {
      return Promise.resolve(this.cachedToken);
    }

    // Deduplicate concurrent calls - if there's already a request in-flight, reuse it
    if (this.pendingTokenRequest) {
      return this.pendingTokenRequest;
    }

    this.pendingTokenRequest = new Promise<string | null>((resolve) => {
      try {
        if (typeof window === "undefined") return resolve(null);

        // Modo "atuar como aluno": o token correto está em welltrainer_token e foi
        // emitido pela API (issuer .../impersonation). NÃO ir no Cognito SDK,
        // que retornaria o token do personal e quebraria as rotas @ApenasAluno().
        if (localStorage.getItem("welltrainer_impersonation_atual")) {
          const impToken = localStorage.getItem("welltrainer_token");
          if (impToken && impToken.startsWith("eyJ")) {
            if (!this.isTokenExpired(impToken)) {
              this.cachedToken = impToken;
              this.cachedTokenExpiry = Date.now() + AuthService.TOKEN_CACHE_TTL;
              return resolve(impToken);
            }
            // Token de impersonation expirou (24h), handleSessionLost vai
            // restaurar o personal automaticamente quando o apiFetch chamar.
          }
          return resolve(null);
        }

        const userPool = new CognitoUserPool({
          UserPoolId:
            process.env.NEXT_PUBLIC_COGNITO_USER_POOL_ID ||
            "us-east-1_QWYvcmVDY",
          ClientId: this.cognitoClientId,
        });

        // Sem sessão Cognito (ex: login via Google), vale o token guardado na
        // entrada, renovado via API quando vence.
        const usarTokenGuardado = (entregar: (v: string | null) => void) => {
          const storedToken = localStorage.getItem("welltrainer_token");
          if (storedToken && storedToken.startsWith("eyJ")) {
            if (!this.isTokenExpired(storedToken)) {
              this.cachedToken = storedToken;
              this.cachedTokenExpiry = Date.now() + AuthService.TOKEN_CACHE_TTL;
              return entregar(storedToken);
            }
            // Token expirado, tentar renovar via API
            this.refreshTokenViaApi().then(entregar);
            return;
          }
          entregar(null);
        };

        const cognitoUser = userPool.getCurrentUser();
        if (!cognitoUser) return usarTokenGuardado(resolve);

        // getSession() dispara um refresh de token na AWS quando a sessão está
        // expirada. O SDK do Cognito NÃO tem timeout: se essa requisição travar
        // (bloqueador de anúncios/privacidade ou proxy bloqueando
        // cognito-idp.*.amazonaws.com, comum em browser desktop, raro no
        // WebView), o callback nunca é chamado e a Promise nunca resolve,
        // deixando a tela em loading infinito. O timeout abaixo converte essa
        // trava numa recuperação via nossa própria API.
        let settled = false;
        const done = (v: string | null) => {
          if (settled) return;
          settled = true;
          clearTimeout(sessionTimer);
          resolve(v);
        };
        const fallback = () => {
          if (settled) return;
          // refreshTokenViaApi bate em /usuario/refresh-token (nosso domínio,
          // o mesmo que o WebView usa), não no endpoint do Cognito SDK.
          this.refreshTokenViaApi().then((token) => {
            if (token) return done(token);
            // Último recurso: token armazenado ainda dentro da validade.
            const stored = localStorage.getItem("welltrainer_token");
            if (
              stored &&
              stored.startsWith("eyJ") &&
              !this.isTokenExpired(stored)
            ) {
              this.cachedToken = stored;
              this.cachedTokenExpiry = Date.now() + AuthService.TOKEN_CACHE_TTL;
              return done(stored);
            }
            this.cachedToken = null;
            this.cachedTokenExpiry = 0;
            done(null);
          });
        };
        const sessionTimer = setTimeout(fallback, 8000);

        const aplicarSessao = (session: any) => {
          const token = session.getIdToken().getJwtToken();
          // Cache the token
          this.cachedToken = token;
          this.cachedTokenExpiry = Date.now() + AuthService.TOKEN_CACHE_TTL;
          // Update stored token so other parts of the app stay in sync
          localStorage.setItem("welltrainer_token", token);
          // Persistir refresh token pra fallback se o storage do Cognito SDK
          // sumir (WKWebView iOS com ITP/pressao de memoria perde as chaves
          // CognitoIdentityServiceProvider.* e o fallback via API precisa
          // dele pra renovar sessao sem forcar logout).
          try {
            const rt = session.getRefreshToken()?.getToken();
            if (rt) localStorage.setItem("welltrainer_refresh_token", rt);
          } catch {}
          done(token);
        };

        cognitoUser.getSession((err: Error | null, session: any) => {
          if (settled) return;
          if (err || !session || !session.isValid()) {
            // Cognito SDK falhou, tentar refresh via API como fallback
            fallback();
            return;
          }

          /* A sessão que o SDK guardou é de OUTRA conta: ela sai do aparelho, e o
             token guardado na entrada assume.

             O SDK guarda a última conta que entrou por SENHA, e quem entra pelo
             Google, pela Apple ou por QR não passa por ele. Uma senha que o
             Cognito aceitava e a API recusava (login de conta apagada) deixava a
             sessão no aparelho, e ela ganhava do login seguinte: todo pedido saía
             com o token da conta velha, a API respondia 401 (que não entra no
             `api_log`) e a home mostrava "Não foi possível carregar seu painel"
             logo depois de um login que deu certo. O `aplicarSessao` ainda
             gravava o token velho por cima do novo. Foi o Wallace, em 22/10/2026:
             a senha de um login de aluno apagado às 15:02 e a conta de personal
             pelo Google às 15:26. */
          if (this.sessaoDoCognitoDeOutraConta(session)) {
            cognitoUser.signOut();
            usarTokenGuardado(done);
            return;
          }

          /* O token no fim da vida é renovado aqui, e não entregue.

             O `isValid()` do SDK desconta um desvio de relógio medido quando a
             sessão nasceu (agora menos a hora de emissão do token), e esse
             desvio carrega junto a demora da rede: o SDK passa a achar o token
             válido alguns segundos depois de o servidor já recusar. Foi o 401 do
             "Pedir o CPF no app" em 14/09/2026: o token venceu às 16:50:21, o
             pedido saiu às 16:50:22, e a nova tentativa depois do 401 recebeu o
             MESMO token, porque para o SDK a sessão ainda valia.

             A conta usa o mesmo desvio do SDK (e não o relógio cru do aparelho):
             num celular com a hora errada, o relógio cru acharia TODO token
             vencido e renovaria a cada pedido. Faltando menos de 60s, que é a
             margem do `isTokenExpired`, pede um token novo. */
          const idToken = session.getIdToken();
          const desvio =
            typeof session.getClockDrift === "function" ? Number(session.getClockDrift()) || 0 : 0;
          const agora = Math.floor(Date.now() / 1000) - desvio;
          const refreshToken = session.getRefreshToken?.();
          if (idToken.getExpiration() - agora < 60 && refreshToken) {
            cognitoUser.refreshSession(refreshToken, (erroRenovar: Error | null, nova: any) => {
              if (settled) return;
              if (erroRenovar || !nova || !nova.isValid()) {
                fallback();
                return;
              }
              aplicarSessao(nova);
            });
            return;
          }

          aplicarSessao(session);
        });
      } catch {
        this.cachedToken = null;
        this.cachedTokenExpiry = 0;
        resolve(null);
      }
    }).finally(() => {
      this.pendingTokenRequest = null;
    });

    return this.pendingTokenRequest;
  }

  isAuthenticated(): boolean {
    try {
      if (typeof window === "undefined" || typeof localStorage === "undefined")
        return false;
      const token = localStorage.getItem("welltrainer_token");
      return !!token && token.startsWith("eyJ");
    } catch {
      return false;
    }
  }

  getCurrentUser(): any {
    try {
      if (typeof window === "undefined" || typeof localStorage === "undefined")
        return null;
      const userStr = localStorage.getItem("welltrainer_user");
      if (!userStr) return null;
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  }

  private saveAuthData(authData: AuthResponse): void {
    try {
      if (
        typeof window !== "undefined" &&
        typeof localStorage !== "undefined"
      ) {
        localStorage.setItem("welltrainer_token", authData.token);
        localStorage.setItem("welltrainer_user", JSON.stringify(authData.user));
        // Limpar cache de assinatura para não herdar status de outro usuário
        localStorage.removeItem("welltrainer_assinatura_status");
        /* Sessão nova apaga o modo "acessar como aluno". Sem isto, o marcador de
           uma impersonação que não foi encerrada pela faixa (a pessoa fechou o
           app, ou saiu pelo botão Sair) sobrevivia no aparelho: a próxima conta a
           entrar nascia "vendo o app como" outra pessoa, com a faixa amarela no
           nome errado e a anamnese pendente do aluno de verdade sem abrir. */
        localStorage.removeItem("welltrainer_impersonation_atual");
        localStorage.removeItem("welltrainer_impersonation_origem");
        /* Idem: limpa o que sobrou do onboarding antigo neste navegador. */
        localStorage.removeItem("welltrainer_onboarding_pending");
        localStorage.removeItem("welltrainer_onboarding_done");
        /* E a leitura do passo a passo do começo: sessão nova começa perguntando
           ao servidor, em vez de confiar no que a conta anterior deixou. */
        esquecerTrilho();

        /* Este aparelho passa a ser desta conta, e o push tem que ir junto.
           Quem entra e sai, ou troca de conta no mesmo celular, dependia da tela
           do painel montar e da marca do aparelho concordar: bastava uma delas
           falhar para a pessoa ficar sem notificação nenhuma sem saber por quê.
           Aqui é o caminho por onde TODA entrada passa (senha, Google, Apple,
           cadastro e QR), e o registro sai forçado, ignorando a marca.

           Import dinâmico e sem esperar: registrar é efeito colateral do login,
           não pode atrasar nem derrubar a entrada. */
        import("../utils/push-registro")
          .then(({ sincronizarPushToken }) => sincronizarPushToken({ forcar: true }))
          .catch(() => {});
      }
    } catch (error) {
      console.error("Error saving auth data:", error);
    }
  }
}

export default new AuthService();
